// frontend/src/pages/visitor/ContributionVerification.tsx

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import PaymentService from '../../services/payment.service';
import CoupleService from '../../services/couple.service';
import BalanceService from '../../services/balance.service';
import { formatPriceSimple, SupportedCurrency } from '../../utils/currency';
import VisitorLayout from '../../components/layouts/VisitorLayout';
import { Couple as BaseCouple } from '../../types';
import { useAuth } from '../../contexts/AuthContext'; // Import auth context

// Extend the base Couple type to include userId
interface ExtendedCouple extends BaseCouple {
  userId?: string;
}

interface DebugInfo {
  reference?: string | null;
  timestamp?: string;
  searchParams?: Record<string, string>;
  localStorage?: {
    contributionCoupleUrl?: string | null;
    paystack_reference?: string | null;
  };
  verifyResponse?: any;
  verifyError?: string;
  coupleData?: any;
  balanceRefreshAttempted?: boolean;
  balanceRefreshSuccess?: boolean;
  balanceData?: any;
  [key: string]: any;
}
interface VerificationResponse {
    success: boolean;
    message?: string;
    isContribution?: boolean;
    contribution?: Contribution;
    [key: string]: any;
  }

interface Contribution {
  _id: string;
  amount: number;
  contributorName: string;
  contributorEmail?: string;
  message?: string;
  currency?: string;
  couple?: string | CoupleResponse; 
  type?: 'gift' | 'wedding_fund' | 'honeymoon' | 'other';
}

// Interface for couple object that might be returned in the API response
interface CoupleResponse {
  _id: string;
  customUrl?: string;
  names?: string;
  bridesName?: string;
  groomsName?: string;
  userId?: string; // Add userId to help with balance updates
  [key: string]: any;
}

const ContributionVerification: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth(); // Get auth state
  const [verifying, setVerifying] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [contributionDetails, setContributionDetails] = useState<Contribution | null>(null);
  const [customUrl, setCustomUrl] = useState<string | null>(null);
  const [coupleName, setCoupleName] = useState<string>('');
  const [balanceUpdated, setBalanceUpdated] = useState<boolean>(false);
  const [debugInfo, setDebugInfo] = useState<DebugInfo>({
    timestamp: new Date().toISOString(),
    balanceRefreshAttempted: false,
    balanceRefreshSuccess: false
  });

  // Function to refresh balance after successful contribution
  const refreshUserBalance = async (coupleUserId?: string) => {
    try {
      setDebugInfo(prev => ({ ...prev, balanceRefreshAttempted: true }));
      console.log('Attempting to refresh balance after contribution');
      
      // Only refresh if user is authenticated (they're the couple owner)
      if (isAuthenticated) {
        // Add a small delay to ensure the transaction has been processed
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Refresh balance using the enhanced method
        const balanceData = await BalanceService.refreshBalance();
        console.log('Balance refreshed successfully:', balanceData);
        
        setBalanceUpdated(true);
        setDebugInfo(prev => ({ 
          ...prev, 
          balanceRefreshSuccess: true,
          balanceData
        }));
        
        // Show a success message with balance info
        toast.success(`Your balance has been updated!`, {
          position: 'bottom-right'
        });
      } else {
        console.log('User not authenticated, skipping balance refresh');
      }
    } catch (error: unknown) {
      console.error('Failed to refresh balance:', error);
      setDebugInfo(prev => ({ 
        ...prev, 
        balanceRefreshSuccess: false,
        balanceRefreshError: error instanceof Error ? error.message : 'Unknown error'
      }));
    }
  };

  useEffect(() => {
    const verifyContribution = async () => {
      try {
        // Extract search params
        const searchParamsObj: Record<string, string> = {};
        searchParams.forEach((value, key) => {
          searchParamsObj[key] = value;
        });
  
        // Get reference from URL params or localStorage backup
        const reference = searchParams.get('reference') || localStorage.getItem('paystack_reference');
        const savedCoupleUrl = localStorage.getItem('contributionCoupleUrl');
  
        // Update debug info
        setDebugInfo(prev => ({
          ...prev,
          reference,
          searchParams: searchParamsObj,
          isAuthenticated,
          localStorage: {
            contributionCoupleUrl: savedCoupleUrl,
            paystack_reference: localStorage.getItem('paystack_reference')
          }
        }));
  
        // Validation
        if (!reference) {
          throw new Error('No payment reference found. Please try again or contact support.');
        }
  
        console.log('Verifying contribution payment with reference:', reference);
        
        // Use the appropriate verification method based on authentication status
        let response: VerificationResponse; // Properly type the response
        if (isAuthenticated) {
          // If user is authenticated, use the authenticated endpoint
          console.log('Using authenticated verification endpoint');
          response = await PaymentService.verifyPaystackTransaction(reference);
        } else {
          // Otherwise use the public endpoint
          console.log('Using public verification endpoint');
          response = await PaymentService.publicVerifyTransaction(reference);
        }
        
        setDebugInfo(prev => ({ 
          ...prev, 
          verifyResponse: response,
          responseType: typeof response
        }));
  
        if (response && response.success) {
          console.log('Contribution verified successfully:', response);
          setSuccess(true);
          
          if (response.contribution) {
            setContributionDetails(response.contribution);
            let coupleUserId: string | undefined;
            
            // Try to get couple details if possible
            if (typeof response.contribution.couple === 'string') {
              try {
                // Get couple details using the ID
                const coupleData = await CoupleService.getCoupleById(response.contribution.couple);
                
                // Cast the response to ExtendedCouple to access userId
                const extendedCouple = coupleData as ExtendedCouple;
                
                setDebugInfo(prev => ({ ...prev, coupleData: extendedCouple }));
                
                if (extendedCouple && extendedCouple.customUrl) {
                  setCustomUrl(extendedCouple.customUrl);
                }
                
                if (extendedCouple) {
                  setCoupleName(extendedCouple.names || '');
                  coupleUserId = extendedCouple.userId;
                }
              } catch (coupleError) {
                console.log('Error fetching couple details:', coupleError);
                // Fallback to saved URL
                if (savedCoupleUrl) {
                  setCustomUrl(savedCoupleUrl);
                }
              }
            } else if (
              response.contribution.couple && 
              typeof response.contribution.couple === 'object'
            ) {
              // Type assertion to help TypeScript understand the structure
              const coupleObj = response.contribution.couple as CoupleResponse;
              
              // Couple data is already included in the response
              if (coupleObj.customUrl) {
                setCustomUrl(coupleObj.customUrl);
              }
              
              if (coupleObj.names) {
                setCoupleName(coupleObj.names);
              }
              
              coupleUserId = coupleObj.userId;
            }
            
            // Try to refresh the balance if the user is authenticated
            // and is likely the owner of the couple receiving the contribution
            await refreshUserBalance(coupleUserId);
          }
          
          // Get the custom URL for redirection
          if (!customUrl && savedCoupleUrl) {
            setCustomUrl(savedCoupleUrl);
          }
          
          toast.success('Thank you for your contribution!');
        } else {
          // This handles the case where verification succeeded but returned false
          throw new Error(response?.message || 'Contribution verification failed');
        }
      } catch (error: any) {
        console.error('Contribution verification error:', error);
        setError(error.message || 'Contribution verification failed');
        setDebugInfo(prev => ({ 
          ...prev, 
          verifyError: error instanceof Error ? error.message : 'Unknown error',
          errorStack: error instanceof Error ? error.stack : undefined
        }));
      } finally {
        setVerifying(false);
      }
    };

    verifyContribution();
  }, [navigate, searchParams, isAuthenticated]);

  const getContent = () => {
    // Show loading state during verification
    if (verifying) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
          <h2 className="text-xl font-semibold mb-2">Verifying your contribution...</h2>
          <p className="text-gray-600">Please wait while we confirm your gift.</p>
        </div>
      );
    }

    // Show success state
    if (success && contributionDetails) {
      const currency = (contributionDetails.currency || 'NGN') as SupportedCurrency;
      
      return (
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <div className="text-green-600 text-5xl mb-4">✅</div>
          <h2 className="text-2xl font-semibold mb-2">Thank You!</h2>
          <p className="text-gray-600 mb-6">
            Your contribution to {coupleName ? coupleName + "'s" : "the couple's"} wedding has been received and confirmed.
          </p>
          
          <div className="bg-white rounded-lg shadow-md p-6 max-w-md w-full mb-6">
            <h3 className="font-semibold text-lg mb-4 border-b pb-2">Contribution Details</h3>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Amount:</span>
                {/* Fixed text color to ensure visibility */}
                <span className="font-medium text-gray-900">
                  {formatPriceSimple(contributionDetails.amount, currency)}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">From:</span>
                {/* Fixed text color to ensure visibility */}
                <span className="font-medium text-gray-900">
                  {contributionDetails.contributorName}
                </span>
              </div>
              
              {contributionDetails.type && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Type:</span>
                  {/* Fixed text color to ensure visibility */}
                  <span className="font-medium text-gray-900 capitalize">
                    {contributionDetails.type.replace('_', ' ')}
                  </span>
                </div>
              )}
              
              {contributionDetails.message && (
                <div className="mt-4">
                  <p className="text-gray-600 mb-1">Your Message:</p>
                  {/* Fixed text color to ensure visibility */}
                  <p className="italic bg-gray-50 p-3 rounded text-gray-800">
                    {contributionDetails.message}
                  </p>
                </div>
              )}
              
              {/* Show balance update status for authenticated users */}
              {isAuthenticated && (
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center">
                    <span className={`w-3 h-3 rounded-full mr-2 ${balanceUpdated ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                    <span className="text-gray-700 text-sm">
                      {balanceUpdated 
                        ? 'Your account balance has been updated!' 
                        : 'Balance update is being processed...'}
                    </span>
                  </div>
                  {balanceUpdated && (
                    <Link
                      to="/admin/balance"
                      className="text-xs text-primary hover:text-primary-dark mt-1 inline-block"
                    >
                      View your updated balance →
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex gap-4 flex-wrap justify-center">
            {customUrl ? (
              <Link
                to={`/wedding/${customUrl}`}
                className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition"
              >
                Return to Wedding Page
              </Link>
            ) : (
              <Link
                to="/"
                className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition"
              >
                Return to Home
              </Link>
            )}
            
            {isAuthenticated && (
              <Link
                to="/admin"
                className="px-6 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900 transition"
              >
                Go to Dashboard
              </Link>
            )}
          </div>
        </div>
      );
    }

    // Show error state
    if (error) {
      return (
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <div className="text-red-600 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold mb-2">Verification Failed</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          
          <div className="flex gap-4 flex-wrap justify-center">
            {customUrl ? (
              <Link
                to={`/wedding/${customUrl}`}
                className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark transition"
              >
                Return to Wedding Page
              </Link>
            ) : (
              <Link
                to="/"
                className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark transition"
              >
                Return to Home
              </Link>
            )}
            
            {isAuthenticated && (
              <Link
                to="/admin"
                className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-800 transition"
              >
                Go to Dashboard
              </Link>
            )}
          </div>
        </div>
      );
    }

    // This should never show, but added as a fallback
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <p>Processing your contribution...</p>
      </div>
    );
  };

  return (
    <VisitorLayout>
      <div className="container mx-auto">
        {getContent()}
        
        {/* Debug information - only visible in development */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-8 p-4 border border-gray-300 rounded max-w-2xl mx-auto mb-8">
            <h3 className="font-semibold mb-2">Debug Information:</h3>
            <pre className="text-xs overflow-auto bg-gray-100 p-2 rounded">
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </VisitorLayout>
  );
};

export default ContributionVerification;